import clsx from 'clsx'
import {
  attachmentsSection,
  jumpToQuestion,
  resetUnsavedFieldsWithNa,
  resetUnsavedValues,
  saveFormInstance,
  selectActiveSection,
  selectQuestions,
  selectSections,
  updateActiveSection,
} from 'components/QuestionnaireRequestAutomated/processor/slices/formInstanceSlice'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addListener } from '@reduxjs/toolkit'
import PreviousAnsweredFileFields from './PreviousAnsweredFileFields'
import AutoQuestionnaireAttachments from '../../Attachments'
import AllFields from './allFields'
import { useEventListeners, useEventManager } from '@deathbyjer/react-event-manager'

export const TAB = {
  INCOMPLETE: 1,
  ALL: 2,
}

const FormSectionHeader = ({ activeTab, onChange }) => {
  const sections = useSelector(selectSections)
  const activeSection = useSelector(selectActiveSection)

  return (
    <div className="dome-d-flex dome-justify-between">
      <span className="dome-h3 dome-color-darkest-grey">{sections[activeSection]?.section_attributes?.label}</span>
      <div className="dome-d-flex">
        <span
          style={{ paddingBottom: '3px' }}
          onClick={() => onChange(TAB.INCOMPLETE)}
          aria-selected={activeTab === TAB.INCOMPLETE}
          className={clsx(
            'questionnaire-request-interstitial-field-main-item dome-p2  dome-cursor-pointer',
            activeTab === TAB.INCOMPLETE ? 'dome-color-darkest-grey' : 'dome-color-med-grey'
          )}
        >
          incomplete
        </span>
        <span
          onClick={() => onChange(TAB.ALL)}
          aria-selected={activeTab === TAB.ALL}
          style={{ padding: '0px 12px 3px 12px' }}
          className={clsx(
            'questionnaire-request-interstitial-field-main-item dome-p2  dome-cursor-pointer',
            activeTab === TAB.ALL ? 'dome-color-darkest-grey' : 'dome-color-med-grey'
          )}
        >
          all fields
        </span>
      </div>
    </div>
  )
}

export const AllDoneQuestions = () => {
  return (
    <div className="questionnaire-request-interstitial-field-done-questions">
      <div className="dome-d-flex dome-flex-column">
        <span className="dome-p2 dome-color-med-grey">You’re all done here! </span>
        <span className="dome-p2 dome-color-med-grey">
          Select “all fields” to view completed questions inside this section.
        </span>
      </div>
    </div>
  )
}

const FormSection = () => {
  const dispatch = useDispatch()
  const events = useEventManager()
  const [tab, setTab] = useState(TAB.INCOMPLETE)
  const sections = useSelector(selectSections)
  const activeSection = useSelector(selectActiveSection)
  const questions = useSelector((state) => selectQuestions(state, tab === TAB.ALL))
  const [activeQuestion, setActiveQuestion] = useState(null)

  const isLastSection = activeSection === sections.length - 1
  const isAllQuestionFilled = questions?.length === 0

  const save = async (e) => {
    e.preventDefault()
    dispatch(saveFormInstance())
    if (activeSection < sections.length - 1) dispatch(updateActiveSection(activeSection + 1))
  }

  const btnNavigationText = useMemo(() => {
    if (isLastSection) return 'save'
    if (isAllQuestionFilled) return 'next'
    return 'save + next'
  }, [isLastSection, isAllQuestionFilled])

  useEventListeners({
    'fields::switchTab': ({ sectionIndex, nodeIndex, fieldId, tab }) => {
      if (tab) setTab(tab)
      setActiveQuestion(fieldId)
    },
  })

  return (
    <div className="dome-d-flex dome-flex-column dome-gap24">
      <FormSectionHeader activeTab={tab} onChange={(selectedTab) => setTab(selectedTab)} />
      <form onSubmit={save} className="dome-d-flex dome-flex-column dome-gap24">
        {questions?.length ? <AllFields questions={questions} activeQuestion={activeQuestion} /> : <AllDoneQuestions />}
        {(!isLastSection || !isAllQuestionFilled) && (
          <div className="dome-d-flex dome-flex-row-reverse">
            <button
              type="submit"
              className={clsx(
                'dome-btn dome-btn-base',
                isAllQuestionFilled ? 'dome-btn-grey-stroke' : 'dome-btn-green-stroke'
              )}
            >
              {btnNavigationText}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}

function UploadAdditionalFiles({ initialAttachments }) {
  return (
    <div style={{ padding: '24px 32px', borderRadius: 6, background: 'white' }}>
      <div className="dome-d-flex dome-flex-column dome-gap12">
        <span className="dome-p2 dome-dark-grey">upload additional files</span>
        <AutoQuestionnaireAttachments initialAttachments={initialAttachments} />
      </div>
    </div>
  )
}

const AttachmentsSection = ({ initialAttachments }) => {
  return (
    <div className="dome-d-flex dome-flex-column dome-gap24">
      <span className="dome-h3 dome-color-darkest-grey">upload attachments</span>
      <div className="dome-d-flex dome-flex-column dome-gap12">
        <PreviousAnsweredFileFields />
        <UploadAdditionalFiles initialAttachments={initialAttachments} />
      </div>
    </div>
  )
}

function Main(props) {
  const dispatch = useDispatch()
  const activeSection = useSelector(selectActiveSection)
  const events = useEventManager()

  useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        actionCreator: jumpToQuestion,
        effect: (action, listenerApi) => {
          const { sectionIndex, nodeIndex, fieldId, tab } = action.payload
          dispatch(updateActiveSection(sectionIndex))
          setTimeout(() => events.applyEventListeners('fields::switchTab', { sectionIndex, nodeIndex, fieldId, tab }))
        },
      })
    )

    return unsubscribe
  }, [dispatch])

  useEffect(() => {
    dispatch(updateActiveSection(0))
    dispatch(resetUnsavedValues())
    dispatch(resetUnsavedFieldsWithNa())
  }, [])

  return (
    <div className="questionnaire-request-interstitial-field-main-container">
      <div className="questionnaire-request-interstitial-field-main-content">
        {activeSection === attachmentsSection ? (
          <AttachmentsSection initialAttachments={props.initialAttachments} />
        ) : (
          <FormSection />
        )}
      </div>
    </div>
  )
}

export default Main
